import { message, Modal } from 'antd';
import { getOaUrlPath } from './utils';

/**
 * TeamOA 登录状态全局管理器（单例模式）
 * 负责管理所有 OAProcessModal 实例的登录状态共享
 *
 * 核心功能：
 * 1. 统一管理 window.open 登录弹窗
 * 2. 内存缓存 teamoa 的完整 localStorage 数据
 * 3. 多实例共享登录状态，避免重复登录
 * 4. 提供登录队列机制，同时只有一个登录弹窗
 */
class TeamOALoginManager {
  private static instance: TeamOALoginManager;

  // 存储 teamoa 的完整 localStorage 数据
  private teamoaStorage: Map<string, any> = new Map();

  // 当前的登录弹窗引用
  private loginPopup: Window | null = null;

  // 是否正在登录中
  private isLoggingIn: boolean = false;

  // 等待登录完成的回调队列
  private pendingCallbacks: Array<(success: boolean) => void> = [];

  // 消息监听器
  private messageListener: ((event: MessageEvent) => void) | null = null;

  // 弹窗关闭检查定时器
  private popupCheckInterval: NodeJS.Timeout | null = null;

  // 登录超时定时器
  private loginTimeoutTimer: NodeJS.Timeout | null = null;

  //  登录状态变化监听器列表
  private loginStateChangeListeners: Array<() => void> = [];

  private constructor() {
    this.initMessageListener();
  }


  /**
   * 获取单例实例
   */
  public static getInstance(): TeamOALoginManager {
    if (!TeamOALoginManager.instance) {
      TeamOALoginManager.instance = new TeamOALoginManager();
    }
    return TeamOALoginManager.instance;
  }

  /**
   * 初始化消息监听器
   */
  private initMessageListener() {
    this.messageListener = (event: MessageEvent) => {

      // 接收来自 popup 的完整 localStorage 数据
      if (event.data.type === 'POPUP_LOGIN_SUCCESS') {
        const { storage } = event.data.data || {};

        // 存储到内存中
        if (storage && typeof storage === 'object') {
          this.teamoaStorage.clear();
          Object.entries(storage).forEach(([key, value]) => {
            this.teamoaStorage.set(key, value);
          });

        } else {
          console.warn('[TeamOALoginManager] storage 数据无效');
        }

        // 清理定时器
        this.clearTimers();

        // 关闭弹窗
        if (this.loginPopup && !this.loginPopup.closed) {
          this.loginPopup.close();
        }

        this.isLoggingIn = false;
        this.loginPopup = null;

        // 通知所有等待的回调
        const callbackCount = this.pendingCallbacks.length;
        this.pendingCallbacks.forEach(callback => callback(true));
        this.pendingCallbacks = [];

        //  通知所有监听器（包括其他已打开的实例）
        this.loginStateChangeListeners.forEach(listener => {
          try {
            listener();
          } catch (error) {
            console.error('[TeamOALoginManager] 触发监听器失败:', error);
          }
        });

        message.success('TeamOA 登录成功，信息已同步');
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * 清理所有定时器
   */
  private clearTimers() {
    if (this.popupCheckInterval) {
      clearInterval(this.popupCheckInterval);
      this.popupCheckInterval = null;
    }

    if (this.loginTimeoutTimer) {
      clearTimeout(this.loginTimeoutTimer);
      this.loginTimeoutTimer = null;
    }
  }

  /**
   * 检查是否已有登录信息
   */
  public hasLoginInfo(): boolean {
    const hasInfo = this.teamoaStorage.size > 0;
    return hasInfo;
  }

  /**
   * 获取存储的 localStorage 数据
   */
  public getStorage(): Record<string, any> {
    const storage: Record<string, any> = {};
    this.teamoaStorage.forEach((value, key) => {
      storage[key] = value;
    });

    return storage;
  }

  /**
   *  清除缓存中的 localStorage 数据
   */

  public clearStorage():void {
    this.teamoaStorage.clear();
  }

  /**
   * 打开登录弹窗（如果正在登录中，则加入等待队列）
   * @returns Promise<boolean> 登录是否成功
   */
  public async requestLogin(): Promise<boolean> {
    // 如果已经有登录信息，直接返回成功
    if (this.hasLoginInfo()) {
      return true;
    }

    // 如果正在登录中，加入等待队列
    if (this.isLoggingIn) {
      return new Promise((resolve) => {
        this.pendingCallbacks.push(resolve);
      });
    }

    // 开始登录流程
    this.isLoggingIn = true;

    return new Promise((resolve) => {
      const IFRAME_BASE_URL = getOaUrlPath();
      const loginUrl = `${IFRAME_BASE_URL}/user/login?source=qdesk&action=sync_storage`;

      // 打开登录弹窗
      const popup = window.open(
        loginUrl,
        'teamoa_login',
        'width=600,height=700,left=400,top=100,toolbar=no,menubar=no,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        // 弹窗被拦截
        this.isLoggingIn = false;
        message.error('弹窗被浏览器拦截，请允许弹窗后重试');

        Modal.confirm({
          title: '弹窗被拦截',
          content: '浏览器阻止了弹窗，请点击地址栏的弹窗拦截图标允许此站点弹出窗口，或点击下方按钮在新标签页打开。登录成功后请手动刷新此页面。',
          okText: '在新标签页打开',
          cancelText: '取消',
          onOk: () => {
            window.open(loginUrl, '_blank');
          }
        });

        resolve(false);
        return;
      }


      this.loginPopup = popup;
      this.pendingCallbacks.push(resolve);

      // 监听弹窗关闭（用户手动关闭）
      this.popupCheckInterval = setInterval(() => {

        if (popup.closed) {
          this.clearTimers();
          if (this.isLoggingIn) {
            // 用户手动关闭了弹窗
            this.isLoggingIn = false;
            this.loginPopup = null;

            // 通知所有等待的回调
            this.pendingCallbacks.forEach(callback => callback(false));
            this.pendingCallbacks = [];

            message.warning('登录窗口已关闭');
          }
        }
      }, 500);

      // 设置超时（30s）
      this.loginTimeoutTimer = setTimeout(() => {

        if (this.isLoggingIn && popup && !popup.closed) {
          popup.close();
        }

        this.clearTimers();
        this.isLoggingIn = false;
        this.loginPopup = null;

        this.pendingCallbacks.forEach(callback => callback(false));
        this.pendingCallbacks = [];

        message.warning('登录超时，请重试');
      }, 30 * 1000);
    });
  }

  /**
   * 清除登录信息（用于退出登录）
   */
  public clearStorage() {
    this.teamoaStorage.clear();
  }

  /**
   * 获取调试信息
   */
  public getDebugInfo() {
    return {
      hasLoginInfo: this.hasLoginInfo(),
      storageKeys: Array.from(this.teamoaStorage.keys()),
      storageSize: this.teamoaStorage.size,
      isLoggingIn: this.isLoggingIn,
      pendingCallbacksCount: this.pendingCallbacks.length,
      hasPopup: !!this.loginPopup,
      popupClosed: this.loginPopup ? this.loginPopup.closed : null,
    };
  }

  /**
   *  注册登录状态变化监听器
   * 当登录成功时会触发所有监听器
   * @param listener 回调函数
   * @returns 注销函数
   */
  public onLoginStateChange(listener: () => void): () => void {
    this.loginStateChangeListeners.push(listener);

    // 返回注销函数
    return () => {
      const index = this.loginStateChangeListeners.indexOf(listener);
      if (index > -1) {
        this.loginStateChangeListeners.splice(index, 1);
      }
    };
  }

  /**
   * 销毁管理器
   */
  public destroy() {

    this.clearTimers();

    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.loginPopup && !this.loginPopup.closed) {
      this.loginPopup.close();
    }

    this.teamoaStorage.clear();
    this.pendingCallbacks = [];
    this.loginStateChangeListeners = [];
    this.isLoggingIn = false;
    this.loginPopup = null;
  }
}

// 导出单例
export default TeamOALoginManager;

// 开发环境下挂载到 window 便于调试
if (process.env.NODE_ENV === 'development') {
  (window as any).__teamoaLoginManager = TeamOALoginManager.getInstance();
}
